/* eslint-disable react-hooks/exhaustive-deps */
import { toast } from "sonner";
import { apiRequestImage, apiRequestVideo } from "./apiConfig";
import { useDispatch } from "react-redux";
import { useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";
import { axiosImage } from "./axiosProvider";

const keyData = "eyisionAdmin";
// Session Storage Functions
export const sessionStorageSetItem = (item) => {
  return sessionStorage.setItem(keyData, JSON.stringify(item));
};

export const sessionStorageGetItem = () => {
  const data = window.sessionStorage.getItem(keyData);
  return data ? JSON.parse(data) : null;
};

export const sessionStorageRemoveItem = () => {
  sessionStorage.removeItem(keyData);
};

// Local Storage Functions
export const localStorageSetItem = (item) => {
  localStorage.setItem(keyData, JSON.stringify(item));
};

export const localStorageGetItem = () => {
  const data = localStorage.getItem(keyData);
  return data ? JSON.parse(data) : null;
};

export const localStorageRemoveItem = () => {
  localStorage.removeItem(keyData);
};

// Helper Functions
export const getAuthToken = () => {
  const sessionData = sessionStorageGetItem();
  return sessionData?.token || null;
};

export const getRefreshToken = () => {
  const sessionData = sessionStorageGetItem();
  return sessionData?.refreshToken || null;
};

export const getUserEmail = () => {
  const sessionData = sessionStorageGetItem();
  return sessionData?.email || null;
};

export const getUserId = () => {
  const sessionData = sessionStorageGetItem();
  return sessionData?.userId || null;
};

export const getUserRole = () => {
  const sessionData = sessionStorageGetItem();
  return sessionData?.roleId || null;
};

export const getRandomHexColor = () => {
  return `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`;
};

export const maskEmail = (email = "") => {
  if (!email) {
    const sessionEmail = getUserEmail();
    if (!sessionEmail) return "";
    email = sessionEmail;
  }

  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return email;

  const visibleChars = 2;
  const maskedPart = "*".repeat(Math.max(localPart.length - visibleChars, 1));

  return `${localPart.slice(0, visibleChars)}${maskedPart}@${domain}`;
};

export const formatChatDate = (sentAt) => {
  const messageDate = new Date(sentAt);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday = messageDate.toDateString() === today.toDateString();
  const isYesterday = messageDate.toDateString() === yesterday.toDateString();
  const isThisWeek =
    messageDate > new Date(today.setDate(today.getDate() - today.getDay()));

  if (isToday) return `Today`;
  if (isYesterday) return `Yesterday`;
  if (isThisWeek) {
    return `${messageDate.toLocaleDateString("en-US", { weekday: "long" })}`;
  }

  return messageDate.toLocaleDateString("en-GB");
};

export const uploadFile = async (file, moduleName = "DEFAULT") => {
  if (!file) throw new Error("No file provided");

  const formData = new FormData();

  // Helper to get correct MIME type for files (especially HEIC/HEIF)
  const getCorrectMimeType = (file) => {
    const fileName = file.name?.toLowerCase() || '';
    const fileType = file.type?.toLowerCase() || '';

    // If MIME type is empty or missing, map based on extension
    if (!fileType || fileType === '') {
      const mimeMap = {
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.webp': 'image/webp',
        '.avif': 'image/avif',
        '.heic': 'image/heic',
        '.heif': 'image/heif',
      };

      for (const [ext, mime] of Object.entries(mimeMap)) {
        if (fileName.endsWith(ext)) return mime;
      }
    }

    // Return existing MIME type if valid
    if (fileType && fileType !== 'application/octet-stream') {
      return fileType;
    }

    // Fallback for octet-stream
    return 'application/octet-stream';
  };

  // Create a corrected file if MIME type needs fixing
  const correctedMimeType = getCorrectMimeType(file);
  const fileToUpload = (file.type === correctedMimeType || !correctedMimeType)
    ? file
    : new File([file], file.name, {
      type: correctedMimeType,
      lastModified: file.lastModified
    });

  formData.append("file", fileToUpload);
  formData.append("module", moduleName);


  try {
    const response = await apiRequestImage(
      "POST",
      "/file-uploader/upload",
      formData,
    );
    return response?.data?.imageURL;
  } catch (error) {
    throw error.message || "Upload failed";
  }
};

export const uploadFileMulti = async (file, moduleName = "DEFAULT") => {
  if (!file) throw new Error("No file provided");

  const formData = new FormData();

  // Helper to get correct MIME type for HEIC/HEIF files
  const getCorrectMimeType = (file) => {
    const fileName = file.name?.toLowerCase() || "";
    const fileType = file.type?.toLowerCase() || "";

    // If MIME type is empty or incorrect, map based on extension
    if (!fileType || fileType === "") {
      if (fileName.endsWith(".heic")) return "image/heic";
      if (fileName.endsWith(".heif")) return "image/heif";
      if (fileName.endsWith(".avif")) return "image/avif";
      if (fileName.endsWith(".webp")) return "image/webp";
      if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
        return "image/jpeg";
      if (fileName.endsWith(".png")) return "image/png";
    }

    return fileType;
  };

  // Create corrected file if MIME type is missing
  const correctedMimeType = getCorrectMimeType(file);
  const fileToUpload =
    file.type === correctedMimeType
      ? file
      : new File([file], file.name, {
        type: correctedMimeType,
        lastModified: file.lastModified,
      });

  formData.append("file", fileToUpload);
  formData.append("module", moduleName);

  try {
    const response = await apiRequestImage(
      "POST",
      "/file-uploader/upload-multi",
      formData,
    );
    return response?.data?.imageURLs;
  } catch (error) {
    throw error.message || "Upload failed";
  }
};

export const uploadImageGallery = async (files, isReturnData = false) => {
  if (!files && files.length === 0) throw new Error("No files provided");

  const formData = new FormData();

  files.forEach((file) => {
    // Create a new file with correct MIME type if needed
    const correctedFile = fixFileMimeType(file);
    formData.append("file", correctedFile);
  });

  try {
    const response = await apiRequestImage("POST", "/gallery/upload", formData);
    return response?.data;
  } catch (error) {
    if (isReturnData) throw error;
    throw error.message || "Upload failed";
  }
};

/**
 * Fix file MIME type if browser failed to detect it
 * This commonly happens with HEIC/HEIF files on Windows
 */
const fixFileMimeType = (file) => {
  // If MIME type is properly detected, return file as is
  if (
    file.type &&
    file.type !== "" &&
    file.type !== "application/octet-stream"
  ) {
    console.log(`✅ Correct MIME type for ${file.name}: ${file.type}`);
    return file;
  }

  // Get file extension
  const extension = file.name.split(".").pop()?.toLowerCase();

  // Map extensions to correct MIME types
  const mimeTypes = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",
    heic: "image/heic",
    heif: "image/heif",
    avif: "image/avif",
    tiff: "image/tiff",
    tif: "image/tiff",
    ico: "image/x-icon",
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    mkv: "video/x-matroska",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    aac: "audio/aac",
    pdf: "application/pdf",
  };

  const correctType = mimeTypes[extension];

  if (correctType) {
    console.log(
      `🔧 Fixed MIME type for ${file.name}: ${file.type || "(empty)"} -> ${correctType}`,
    );

    // Create a new File object with the correct MIME type
    return new File([file], file.name, {
      type: correctType,
      lastModified: file.lastModified,
    });
  }

  // If extension is unknown, try to detect from file name patterns
  if (!correctType) {
    console.warn(
      `⚠️ Unknown file type for ${file.name}, keeping original type: ${file.type || "application/octet-stream"}`,
    );

    // For HEIC/HEIF files that might have uppercase extensions
    if (extension === "heic" || extension === "heif") {
      const type = extension === "heic" ? "image/heic" : "image/heif";
      return new File([file], file.name, {
        type,
        lastModified: file.lastModified,
      });
    }
  }

  return file;
};

export const transformArray = (data) => {
  return data?.map((item) => ({
    value: item._id,
    label:
      item?.name ||
      item?.period ||
      item?.dimensions_value ||
      item?.replace_policy ||
      item?.duration ||
      item?.description,
  }));
};

export const validateFiles = (files, maxSizeMB = 5) => {
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/avif",
    "image/heic",
    "image/heif",
  ];

  const allowedExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".avif",
    ".heic",
    ".heif",
  ];
  const maxSize = maxSizeMB * 1024 * 1024;
  const validFiles = [];

  for (const f of files) {
    const fileName = f.name?.toLowerCase() || "";
    const fileType = f.type?.toLowerCase() || "";

    // Check if file type is valid (by MIME type or extension fallback)
    const isValidType =
      allowedTypes.includes(fileType) ||
      allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValidType) {
      toast.error(
        `${f.name} is not a supported image format. Please upload a JPG, JPEG, PNG, WEBP, HEIC, HEIF, or AVIF image.`,
      );
      continue;
    }

    if (f.size > maxSize) {
      toast.error(
        `${f.name} exceeds the maximum allowed size of ${maxSizeMB}MB. Please select a smaller image.`,
      );
      continue;
    }

    validFiles.push(f);
  }

  return validFiles;
};

export const validateVideoFiles = (files) => {
  const IMAGE_TYPES = ["image/jpeg", "image/png"];
  const IMAGE_MAX = 5 * 1024 * 1024; // 5 MB

  const PROMO_VIDEO_TYPES = [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime",
    "video/x-matroska",
  ];
  const PROMO_VIDEO_MAX = 10 * 1024 * 1024; // 10 MB

  const validFiles = [];

  for (const f of files) {
    if (IMAGE_TYPES.includes(f.type)) {
      if (f.size > IMAGE_MAX) {
        toast.error(`${f.name} is too large. Max image size is 5 MB.`);
        continue;
      }
      validFiles.push(f);
    } else if (PROMO_VIDEO_TYPES.includes(f.type)) {
      if (f.size > PROMO_VIDEO_MAX) {
        toast.error(`${f.name} is too large. Max video size is 10 MB.`);
        continue;
      }
      validFiles.push(f);
    } else {
      toast.error(`${f.name} has unsupported type (${f.type}).`);
    }
  }

  return validFiles;
};

export const uploadVideoFile = async (file, moduleName = "DEFAULT") => {
  if (!file) throw new Error("No file provided");

  const formData = new FormData();
  formData.append("file", file);
  formData.append("module", moduleName);
  try {
    const response = await apiRequestVideo(
      "POST",
      "/file-uploader/upload-video",
      formData,
    );
    return response?.data?.imageURL;
  } catch (error) {
    throw error.message || "Upload failed";
  }
};

export const roleBasedAccess = () => {
  return [
    { label: "user", value: 5 },
    { label: "Designer", value: 3 },
  ];
};

export const roles = {
  5: "User",
  3: "Seller",
};

export const useProductDataFetching = (apiCalls, dependencies = []) => {
  const dispatch = useDispatch();

  const fetchAllData = useCallback(
    async (callArray = apiCalls) => {
      try {
        const results = await Promise.allSettled(
          callArray.map(({ action }) => {
            const result = dispatch(action());
            return result.unwrap ? result.unwrap() : result;
          }),
        );

        const failedCalls = results
          .filter(({ status }) => status === "rejected")
          .map((_, index) => callArray[index].name);

        if (failedCalls.length > 0) {
          return {
            error: `Some data failed to load: ${failedCalls.join(
              ", ",
            )}. Please refresh to try again.`,
          };
        }
        return { success: true };
      } catch (err) {
        toast.error(
          err?.message ||
          "Failed to load product data. Please refresh the page and try again.",
        );
        console.error("Error fetching data:", err);
        return { error: err.message };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch, ...dependencies],
  );

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return { fetchAllData };
};

export const useTabScroll = (tabs, activeTab, setActiveTab) => {
  const tabRefs = useRef({});
  const scrollTimeoutRef = useRef(null);

  const scrollToSection = useCallback(
    (tabId) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setActiveTab(tabId);
      const element = tabRefs.current[tabId];
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    [setActiveTab],
  );

  const setupScrollListener = useCallback(
    (container) => {
      if (!container) return;

      const handleScroll = debounce(() => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          const containerRect = container.getBoundingClientRect();
          const containerTop = containerRect.top;
          let closestTab = null;
          let closestDistance = Infinity;

          tabs.forEach((tab) => {
            const element = tabRefs.current[tab.id];
            if (element) {
              const rect = element.getBoundingClientRect();
              const distance = Math.abs(rect.top - containerTop - 20);

              if (distance < closestDistance) {
                closestDistance = distance;
                closestTab = tab.id;
              }
            }
          });

          if (closestTab && closestTab !== activeTab) {
            setActiveTab(closestTab);
          }
        }, 100);
      }, 100);

      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    },
    [activeTab, tabs, setActiveTab],
  );

  return { tabRefs, scrollToSection, setupScrollListener };
};

export const validateCollectionForm = (formValues) => {
  const errors = {};

  if (!formValues.name?.trim()) {
    errors.name = "Collection name is required";
  }

  if (!formValues.thumbnails) {
    errors.thumbnails = "Thumbnail is required";
  }

  return errors;
};

export const validateCategoryForm = (formValues) => {
  const errors = {};

  if (!formValues.name?.trim()) {
    errors.name = "Category name is required";
  }

  if (!formValues.seoUrl || typeof formValues.seoUrl !== "string") {
    errors.seoUrl = "Thumbnail is required";
  }

  if (
    formValues.parentCategory &&
    formValues.parentCategory.value !== "ROOT" &&
    !formValues.parentCategory.type
  ) {
    errors.parentCategory = "Category type is required for sub-categories";
  }

  return errors;
};

export const validateBrandForm = (formValues) => {
  const errors = {};

  if (!formValues.name?.trim()) {
    errors.name = "Brand name is required";
  }

  if (!formValues.thumbnails) {
    errors.thumbnails = "Thumbnail image is required";
  }

  if (!formValues.logo) {
    errors.logo = "Logo image is required";
  }

  return errors;
};

export const getCurrencySymbol = (currencyCode = "USD") => {
  const symbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    INR: "₹",
    AUD: "A$",
    CAD: "C$",
    JPY: "¥",
    CNY: "¥",
    RUB: "₽",
    KRW: "₩",
    AED: "د.إ",
  };

  return symbols[currencyCode.toUpperCase()] || "$";
};

export function capitalizeWords(str = "") {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export const uploadCSV = (file) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    axiosImage
      .post("/product/uploadProductCSV", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const BANNER_POSITION = {
  DASHBOARD: "dashboard",
  POST: "post",
  FORUM_POST: "forum-post",
  SEARCH: "search",
};

export const BANNER_PLACEMENT = {
  Top: "top",
  Middle: "middle",
  Bottom: "bottom",
};
